import type {ItemType} from '@src/constants/item-types.ts';
import type {SortDirection} from '@src/constants/sort.ts';
import type {
  FilterState,
  QuickSearchState,
  StateSlice,
} from '@src/stores/types.ts';
import type {
  QuickSearchBody,
  QuickSearchItem,
  QuickSearchResult,
} from '@src/types/quickSearch.ts';
import type {DateRange} from '@src/types/searchFilters.ts';
import {IMAGERY_CAPABILITIES} from '@src/constants/assetTypes.js';
import {GOOD_ITEM_TYPES} from '@src/constants/item-types.ts';
import {SORT_DIRECTIONS} from '@src/constants/sort.ts';
import {parseSavedSearch} from '@src/stores/parseSavedSearch';
import {
  DEFAULT_VALUES,
  PS_INSTRUMENTS,
  PUBLISHING_STAGES_DEFAULT,
} from '@src/utils/filters.ts';
import {getUniqueId} from '@src/utils/items.js';
import {isEmptyRange} from '@src/utils/searchFilters.js';
import keyBy from 'lodash/keyBy';
import merge from 'lodash/merge';

/**
 * All the state related to a Data API quick-search and its results
 */

export const initialState = {
  dailyEmailEnabled: false,
  id: undefined,
  name: '',
  page: 0,
  itemId: null, // used for single item search
  itemTypes: Object.keys(GOOD_ITEM_TYPES).filter(
    key => GOOD_ITEM_TYPES[key as ItemType],
  ) as ItemType[],
  dateRanges: [],
  filters: {
    imageryCapabilities: [IMAGERY_CAPABILITIES.nir],
    instruments: PS_INSTRUMENTS,
    publishingStages: PUBLISHING_STAGES_DEFAULT,
    ...DEFAULT_VALUES,
  },
  results: {
    loading: false,
    complete: false,
    byId: {},
  },
  selections: {
    focusedFootprints: {},
    hoveredFootprints: {},
    mapViewItemIds: [],
    mapViewResultIds: [],
    orderResultIds: [],
    orderItemIds: [],
  },
  sidebar: {
    activeResultId: null,
    filtersPanelOpen: false,
  },
  sortDirection: SORT_DIRECTIONS.DSC,
};

export const setQuickSearchSlice: StateSlice<QuickSearchState> = (
  set,
  get,
) => ({
  ...initialState,
  selectItemTypes: (itemTypes: ItemType[]) =>
    set(
      state => {
        state.quickSearch.itemTypes = itemTypes;
      },
      undefined,
      'quickSearch/selectItemTypes',
    ),
  setFilters: (filters: FilterState) =>
    set(
      state => {
        state.quickSearch.filters = {
          ...state.quickSearch.filters,
          ...filters,
        };

        state.quickSearch.selections = initialState.selections;
        state.quickSearch.sidebar.activeResultId =
          initialState.sidebar.activeResultId;
      },
      undefined,
      'quickSearch/setFilters',
    ),
  setDateRanges: (ranges: DateRange[]) =>
    set(
      state => {
        state.quickSearch.dateRanges = ranges.filter(
          range => !isEmptyRange(range),
        );
      },
      undefined,
      'quickSearch/setDateRanges',
    ),
  loadNextPage: () =>
    set(
      state => {
        state.quickSearch.page += 1;
      },
      undefined,
      'quickSearch/loadNextPage',
    ),
  setResultsLoading: (isLoading: boolean) =>
    set(
      state => {
        state.quickSearch.results.loading = isLoading;
      },
      undefined,
      'quickSearch/setResultsLoading',
    ),
  setResultsComplete: (isComplete: boolean) =>
    set(
      state => {
        state.quickSearch.results.complete = isComplete;
      },
      undefined,
      'quickSearch/setResultsComplete',
    ),
  addResults: (results: QuickSearchResult[]) =>
    set(
      state => {
        const groupById = keyBy(results, 'id');
        state.quickSearch.results.byId = {
          ...state.quickSearch.results.byId,
          ...groupById,
        };

        // Select results if corresponding items are already selected, as is the
        // case for refreshing or hitting a url with items in the hash.
        const viewResults = results
          .filter(result =>
            result.properties.itemIds.some(id =>
              state.quickSearch.selections.mapViewItemIds.includes(id),
            ),
          )
          .map(r => r.id);

        state.quickSearch.selections.mapViewResultIds = Array.from(
          new Set([
            ...state.quickSearch.selections.mapViewResultIds,
            ...viewResults,
          ]),
        );

        const orderResultIds = results
          .filter(result =>
            result.properties.itemIds.some(id =>
              state.quickSearch.selections.orderItemIds.includes(id),
            ),
          )
          .map(r => r.id);

        state.quickSearch.selections.orderResultIds = Array.from(
          new Set([
            ...state.quickSearch.selections.orderResultIds,
            ...orderResultIds,
          ]),
        );
      },
      undefined,
      'quickSearch/setResults',
    ),
  resetResults: () =>
    set(
      state => {
        state.quickSearch.results = initialState.results;
        state.quickSearch.selections = initialState.selections;
        state.quickSearch.page = initialState.page;
      },
      undefined,
      'quickSearch/resetResults',
    ),

  addResultToMapView: (resultId: string, reset?: boolean) =>
    set(
      state => {
        const resultItemIds =
          state.quickSearch.results.byId[resultId]?.properties.itemIds ?? [];

        if (reset) {
          state.quickSearch.selections.mapViewResultIds = [resultId];
          state.quickSearch.selections.mapViewItemIds = resultItemIds;
        } else {
          state.quickSearch.selections.mapViewResultIds.push(resultId);
          state.quickSearch.selections.mapViewItemIds = Array.from(
            new Set([
              ...state.quickSearch.selections.mapViewItemIds,
              ...resultItemIds,
            ]),
          );
        }
      },
      undefined,
      'quickSearch/addResultToMapView',
    ),
  removeResultFromMapView: (resultId: string) =>
    set(
      state => {
        const resultItemIds =
          state.quickSearch.results.byId[resultId]?.properties.itemIds ?? [];

        state.quickSearch.selections.mapViewResultIds =
          state.quickSearch.selections.mapViewResultIds.filter(
            id => id !== resultId,
          );

        state.quickSearch.selections.mapViewItemIds =
          state.quickSearch.selections.mapViewItemIds.filter(
            id => !resultItemIds.includes(id),
          );
      },
      undefined,
      'quickSearch/removeResultFromMapView',
    ),
  addItemsToMapView: (resultId: string, itemIds: string[]) =>
    set(
      state => {
        if (
          resultId &&
          !state.quickSearch.selections.mapViewResultIds.includes(resultId)
        ) {
          state.quickSearch.selections.mapViewResultIds.push(resultId);
        }

        state.quickSearch.selections.mapViewItemIds = [
          ...state.quickSearch.selections.mapViewItemIds,
          ...itemIds,
        ];
      },
      undefined,
      'quickSearch/addItemsToMapView',
    ),
  removeItemsFromMapView: (resultId: string, itemIds: string[]) =>
    set(
      state => {
        const newItems = state.quickSearch.selections.mapViewItemIds.filter(
          id => !itemIds.includes(id),
        );
        state.quickSearch.selections.mapViewItemIds = newItems;

        const resultItemIds =
          state.quickSearch.results.byId[resultId]?.properties.itemIds ?? [];

        // check if there are ANY items from the result left
        const hasAny = resultItemIds.some(itemId => newItems.includes(itemId));

        if (!hasAny) {
          state.quickSearch.selections.mapViewResultIds =
            state.quickSearch.selections.mapViewResultIds.filter(
              id => id !== resultId,
            );
        }
      },
      undefined,
      'quickSearch/removeItemsFromMapView',
    ),
  removeAllFromMapView: () =>
    set(
      state => {
        state.quickSearch.selections.mapViewResultIds = [];
        state.quickSearch.selections.mapViewItemIds = [];
      },
      undefined,
      'quickSearch/removeAllFromMapView',
    ),
  addResultToOrder: (resultId: string, reset?: boolean) =>
    set(
      state => {
        const result = state.quickSearch.results.byId[resultId] ?? {};
        // only use items the user has access to
        const resultItemIds =
          result.properties?.items
            .filter(item => item.properties?.canDownload)
            .map(getUniqueId) ?? [];

        if (reset) {
          state.quickSearch.selections.orderResultIds = [resultId];
          state.quickSearch.selections.orderItemIds = resultItemIds;
        } else {
          state.quickSearch.selections.orderResultIds.push(resultId);
          state.quickSearch.selections.orderItemIds = Array.from(
            new Set([
              ...state.quickSearch.selections.orderItemIds,
              ...resultItemIds,
            ]),
          );
        }
      },
      undefined,
      'quickSearch/addResultToOrder',
    ),
  removeResultFromOrder: (resultId: string) =>
    set(
      state => {
        const resultItemIds =
          state.quickSearch.results.byId[resultId]?.properties.itemIds || [];

        state.quickSearch.selections.orderResultIds =
          state.quickSearch.selections.orderResultIds.filter(
            id => id !== resultId,
          );

        state.quickSearch.selections.orderItemIds =
          state.quickSearch.selections.orderItemIds.filter(
            id => !resultItemIds.includes(id),
          );
      },
      undefined,
      'quickSearch/removeResultFromOrder',
    ),

  addItemsToOrder: (resultId: string, itemIds: string[]) =>
    set(
      state => {
        if (
          resultId &&
          !state.quickSearch.selections.orderResultIds.includes(resultId)
        ) {
          state.quickSearch.selections.orderResultIds.push(resultId);
        }

        state.quickSearch.selections.orderItemIds = [
          ...state.quickSearch.selections.orderItemIds,
          ...itemIds,
        ];
      },
      undefined,
      'quickSearch/addItemsToOrder',
    ),
  removeItemsFromOrder: (resultId: string, itemIds: string[]) =>
    set(
      state => {
        const newItems = state.quickSearch.selections.orderItemIds.filter(
          id => !itemIds.includes(id),
        );
        state.quickSearch.selections.orderItemIds = newItems;

        const resultItemIds =
          state.quickSearch.results.byId[resultId]?.properties.itemIds ?? [];

        // check if there are ANY items from the result left
        const hasAny = resultItemIds.some(itemId => newItems.includes(itemId));
        if (!hasAny) {
          state.quickSearch.selections.orderResultIds =
            state.quickSearch.selections.orderResultIds.filter(
              id => id !== resultId,
            );
        }
      },
      undefined,
      'quickSearch/removeItemsFromOrder',
    ),
  removeAllFromOrder: () =>
    set(
      state => {
        state.quickSearch.selections.orderResultIds = [];
        state.quickSearch.selections.orderItemIds = [];
      },
      undefined,
      'quickSearch/removeAllFromOrder',
    ),

  setFocusedFootprints: (items: QuickSearchItem[], reset?: boolean) =>
    set(
      state => {
        state.quickSearch.selections.focusedFootprints = {
          ...(reset ? {} : state.quickSearch.selections.focusedFootprints),
          ...Object.fromEntries(
            items.map((item: QuickSearchItem) => [
              getUniqueId(item),
              item.geometry,
            ]),
          ),
        };
      },
      undefined,
      'quickSearch/setFocusedFootprints',
    ),
  removeFocusedFootprintsById: (itemIds: string[]) =>
    set(
      state => {
        itemIds.forEach(id => {
          delete state.quickSearch.selections.focusedFootprints[id];
        });
      },
      undefined,
      'quickSearch/removeFocusedFootprintsById',
    ),
  setHoveredFootprints: (items: QuickSearchItem[]) =>
    set(
      state => {
        state.quickSearch.selections.hoveredFootprints = Object.fromEntries(
          items.map((item: QuickSearchItem) => [
            getUniqueId(item),
            item.geometry,
          ]),
        );
      },
      undefined,
      'quickSearch/setHoveredFootprints',
    ),

  resetSelections: () =>
    set(
      state => {
        state.quickSearch.selections = initialState.selections;
        state.quickSearch.sidebar.activeResultId =
          initialState.sidebar.activeResultId;
      },
      undefined,
      'quickSearch/resetSelections',
    ),
  setActiveResultId: (resultId: string | null) =>
    set(
      state => {
        state.quickSearch.sidebar.activeResultId = resultId;
      },
      undefined,
      'quickSearch/setActiveResultId',
    ),
  showFiltersPanel: (doShow: boolean) =>
    set(
      state => {
        state.quickSearch.sidebar.filtersPanelOpen = doShow;
      },
      undefined,
      'quickSearch/showFiltersPanel',
    ),
  setSortDirection: (direction: SortDirection) =>
    set(
      state => {
        state.quickSearch.sortDirection = direction;
        state.quickSearch.page = initialState.page;
      },
      undefined,
      'quickSearch/showFiltersPanel',
    ),
  searchByItem: (item: QuickSearchItem) =>
    set(
      state => {
        state.quickSearch.itemId = item.id;
        state.quickSearch.name = item.id;
        state.quickSearch.page = initialState.page;
        state.quickSearch.itemTypes = [item.properties?.item_type];
        state.map.feature = {
          type: 'Feature',
          geometry: item.geometry,
          properties: {},
          id: item.id,
        };

        state.quickSearch.sidebar = initialState.sidebar;
      },
      undefined,
      'quickSearch/searchByItem',
    ),

  loadSavedSearch: async (savedSearch: QuickSearchBody) => {
    const {feature, extent, ...parsedSearch} =
      await parseSavedSearch(savedSearch);
    const state = get();

    // empty object to clone safely
    const filters = merge({}, initialState.filters, parsedSearch.filters);

    set(
      {
        quickSearch: {
          ...state.quickSearch, //don't lose the slice functions
          ...initialState, //make sure to reset any state that is not part of the new search
          ...parsedSearch,
          filters,
        },
        map: {
          ...state.map,
          feature,
          fitExtent: extent,
        },
      },
      undefined,
      'quickSearch/loadSavedSearch',
    );
  },
  clearSearch: () =>
    set(
      state => {
        state.quickSearch.name = initialState.name;
        state.quickSearch.id = initialState.id;
        state.quickSearch.dailyEmailEnabled = initialState.dailyEmailEnabled;
        state.quickSearch.itemId = initialState.itemId;

        //ToDo: does clearing also clear out map geometry and filters?
        // is the location search still coupled to this in this app?
      },
      undefined,
      'quickSearch/clearSavedSearch',
    ),
});
